import type { SpeechModel } from 'ai';
import {
  BaseSpeechProvider,
  type SpeechProviderAdapter,
  TaskStatus,
  type CreateTaskConfig,
  type CreateTaskResult,
  type PollTaskConfig,
  type PollTaskResult,
} from '../base';
import { getValueByPath, isStatusMatch } from '../tools';
import { buildInputPayloadFromConfig, getWavespeedModelConfig } from './utils';
import { wavespeedStatusMapping } from './config';
import type { WavespeedCreateResponse, WavespeedTaskResponse } from './types';

const provider = new BaseSpeechProvider(
  {
    baseUrlEnv: 'WAVESPEED_BASE_URL',
    apiKeyEnv: 'WAVESPEED_API_KEY',
    debugEnv: 'WAVESPEED_DEBUG',
    providerName: 'WAVESPEED',
  },
  {
    timeoutMs: 10 * 60 * 1000,
    backoff: {
      initialDelayMs: 3000,
      maxSteps: 20,
      scale: 1,
      jitter: 0.2,
      maxDelayMs: 8000,
    },
  }
);

const adapter: SpeechProviderAdapter = {
  async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
    const config = getWavespeedModelConfig(modelId);
    const payload = await buildInputPayloadFromConfig(modelId, params);
    return {
      url: `${context.baseUrl}${config.apiPath}`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${context.apiKey}`,
      },
      body: payload,
    };
  },

  parseCreateTaskResponse(modelId, response, data: WavespeedCreateResponse): CreateTaskResult {
    const config = getWavespeedModelConfig(modelId);
    const mapping = config.statusMapping || wavespeedStatusMapping;
    const pollUrl =
      getValueByPath(data, mapping.taskIdPath) || (data as any)?.data?.urls?.get;
    if (!pollUrl) {
      throw new Error('Failed to get task id from response');
    }
    // 注意：这里将 pollUrl 作为 taskId 传递，poll 请求直接 GET 该 URL
    return { taskId: String(pollUrl), raw: data };
  },

  buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
    return {
      url: () => taskId,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${context.apiKey}`,
      },
    };
  },

  parsePollTaskResponse(modelId, response, data: WavespeedTaskResponse): PollTaskResult {
    const config = getWavespeedModelConfig(modelId);
    const mapping = config.statusMapping || wavespeedStatusMapping;

    const state = getValueByPath(data, mapping.statePath);
    if (state === undefined) {
      return { status: TaskStatus.PROCESSING, raw: data };
    }

    if (isStatusMatch(state, mapping.success)) {
      const urlValue = getValueByPath(data, mapping.audioUrlPath);
      const audioUrls: string[] = Array.isArray(urlValue)
        ? urlValue.filter(Boolean).map(String)
        : urlValue
          ? [String(urlValue)]
          : [];
      if (!audioUrls.length) {
        return {
          status: TaskStatus.FAILED,
          error: 'Missing audio URL in result',
          raw: data,
        };
      }
      return { status: TaskStatus.SUCCESS, audioUrls, raw: data };
    }

    if (isStatusMatch(state, mapping.failed)) {
      const err = getValueByPath(data, mapping.errorPath);
      return {
        status: TaskStatus.FAILED,
        error: String(err) || 'unknown error',
        raw: data,
      };
    }

    if (isStatusMatch(state, mapping.processing)) {
      return { status: TaskStatus.PROCESSING, raw: data };
    }

    return { status: TaskStatus.PROCESSING, raw: data };
  },
};

export const wavespeed = {
  speech: (modelId: string): SpeechModel => {
    const cfg = getWavespeedModelConfig(modelId);
    return provider.createSpeechModel(modelId, adapter, cfg.timeoutMs);
  },
};
